import Component from '@cx-modules/top-trends';
import TopTrendsCss from '@cx-modules/top-trends/style.css?inline';
import css from '../../utils/css';

import { widgetBootstrap } from '../../services/cx-module-helper';
import { meta } from './meta.js';

export const attributes = meta.attributes;
export const widgetName = meta.name;

const { VITE_SPORTS_DATA_API_HOST: dataApiEndpoint } = import.meta.env;

type Bookmaker = { bookmakerName?: string };

type ContractParams = {
  affIds?: unknown;
  apiKey?: unknown;
  analytics?: { getDataLayer: () => unknown };
  betIds?: unknown;
  bookies?: Record<string, Bookmaker>;
  campaignName?: unknown;
  categoryId?: unknown;
  categoryGroupId?: unknown;
  countryCode?: string;
  enableClickouts?: unknown;
  eventId?: unknown;
  hideBets?: unknown;
  marketId?: unknown;
  limit?: string;
  oddsFormat?: unknown;
  repub?: unknown;
  subdivisionCode?: string;
  subeventId?: unknown;
  widgetStyle?: string;
  hash?: unknown;
};

type ContractProps = {
  affIds?: unknown;
  apiKey?: unknown;
  dataApiEndpoint?: string;
  disableAnalytics: boolean;
  analyticsImpressionEventData: unknown;
  analyticsImpressionEventName: string;
  betIds?: unknown;
  bookies?: Record<string, Bookmaker>;
  categoryId?: unknown;
  categoryGroupId?: unknown;
  countryCode?: string;
  enableClickouts?: unknown;
  eventId?: unknown;
  hideBets?: unknown;
  marketId?: unknown;
  oddsFormat?: unknown;
  repub?: unknown;
  subeventId?: unknown;
  theme?: string;
  limit?: number;
  analyticsDataLayer?: unknown;
  subdivisionCode?: string;
};

export const contract = ({
  affIds,
  apiKey,
  analytics,
  betIds,
  bookies,
  campaignName,
  categoryId,
  categoryGroupId,
  countryCode,
  enableClickouts,
  eventId,
  hideBets,
  marketId,
  limit,
  oddsFormat,
  repub,
  subdivisionCode,
  subeventId,
  widgetStyle: theme,
  hash: widgetSlot
}: ContractParams) => {
  const props: ContractProps = {
    affIds,
    apiKey,
    dataApiEndpoint,
    disableAnalytics: !analytics,
    analyticsImpressionEventData: {
      bookieCode: Object.keys(bookies || {}),
      bookieName: Object.values(bookies || {}).map(b => b.bookmakerName),
      campaignName,
      widgetType: 'top-trends',
      widgetSlot,
      variation: 'carousel-top-trends'
    },
    analyticsImpressionEventName: 'widget_impression',
    betIds,
    bookies,
    categoryId,
    categoryGroupId,
    countryCode: countryCode ?? 'US',
    enableClickouts,
    eventId,
    hideBets,
    marketId,
    oddsFormat,
    repub,
    subeventId,
    theme,
    limit: limit ? parseInt(limit, 10) : undefined
  };

  const countriesRequiringSubdivisionCode = ['US', 'CA'];
  const normalisedCountryCode = props.countryCode?.toUpperCase();

  if (analytics) {
    props.analyticsDataLayer = analytics.getDataLayer();
  }

  if (
    normalisedCountryCode &&
    countriesRequiringSubdivisionCode.includes(normalisedCountryCode)
  ) {
    props.subdivisionCode = subdivisionCode ?? 'NJ';
  }

  return props;
};

// Cache key matching getTopTrendsCacheKey from cx-modules/top-trends
const queryKey = (campaignData: ContractProps) => [
  'top-trends',
  {
    bookmakerCodes: campaignData.bookies
      ? Object.keys(campaignData.bookies)
      : undefined,
    categoryId: campaignData.categoryId,
    eventId: campaignData.eventId,
    limit: campaignData.limit,
    marketId: campaignData.marketId,
    subeventId: campaignData.subeventId
  }
];

// Shadow DOM resets — cxy-components CSS targets `html` and `*` for base resets,
// which don't apply inside shadow DOM, breaking button styles and image sizing.
const customCss = `
  :host {
    box-sizing: border-box;
    text-rendering: optimizelegibility;
    -webkit-font-smoothing: antialiased;
    -moz-font-smoothing: antialiased;
  }

  :host,
  :host *,
  :host *::before,
  :host *::after {
    box-sizing: border-box;
  }

  button {
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    color: inherit;
    font: inherit;
  }

  button img[alt] {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  button[class*='oddsButton'] {
    align-items: center !important;
  }


  button[class*='oddsButton'] [class*='oddsContainer'][class*='direction-row'] [class*='line'],
  button[class*='oddsButton'] [class*='oddsContainer'][class*='direction-row'] [class*='odds'] {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-block-start: 0 !important;
  }

  button[class*='oddsButton'] [class*='oddsContainer'] [class*='odds'] {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-block-start: 0 !important;
  }

  button[class*='oddsButton'] [class*='oddsContainer'] [class*='line'] {
    margin-block-start: 0 !important;
  }
`;

widgetBootstrap({
  attributes,
  contract,
  Component,
  customCss,
  queryKey,
  themeContract: css`
    #root > div {
      --widget-cta-text-color: var(--widget-cta-color);
      --widget-child-background-color: var(--widget-background-color);
      --cxy-color-content-default: var(--widget-text-color);
      --top-trends-border-default: var(--widget-accent-background-color);
      --cxy-color-reference-line-label: var(--widget-background-color);
      --cxy-color-success: var(--widget-positive-color, #23aa23);
      --cxy-color-content-danger: var(--widget-negative-color, #cc0000);

      [class*='_topTrendsCard_'] {
        border-radius: var(--widget-border-radius, 8px);
      }
      --widget-modal-buttons-border-radius-custom: var(
        --widget-cta-border-radius
      );

      /* tooltip button trigger svg bg */
      button[class*='trigger'] svg {
        background: transparent;
      }

      /* tooltip */
      [class*='_compactBidirectionalBarChartWrapper_']
        [class*='recharts-tooltip-wrapper']
        [class*='recharts-default-tooltip'] {
        background-color: var(--widget-text-color) !important;
        * {
          color: var(--widget-background-color);
        }
      }

      [class*='_eventHeader_'] * {
        color: var(--widget-accent-text-color);
      }

      [class*='_betSubtitle_'] {
        color: var(--widget-text-color);
      }

      [class*='_topTrendsCard_'] {
        border-color: var(--widget-accent-background-color);
      }

      button[class*='_oddsButton_'] {
        color: var(--widget-cta-color);
        background-color: var(--widget-cta-background-color);

        &:hover {
          background-color: color-mix(
            in srgb,
            var(--widget-cta-background-color),
            var(--widget-background-color) 10%
          );
        }

        [data-cxy-name='avatar'] {
          border-radius: var(--widget-cta-inner-border-radius, 50%);
        }
      }

      button[class*='_carouselControl_'] {
        position: absolute;

        svg {
          stroke: var(--widget-text-color);
        }
      }
    }
  `,
  style: TopTrendsCss,
  name: widgetName,
  version: '2.0.0' // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
});
